import ModalFormWindow from "@/HOC/ModalFormWindow/ModalFormWindow";
import { FC, FormEvent, useEffect, useRef, useState } from "react";
import { IFormAddingCallingList, TInitialValues } from "./FormAddingCallingList.props";
import { BlockWithError, CustomButton, CustomMultiSelect, CustomSelect, CustomTextfieldWithTouched, TextfieldWithAutocomplete } from "@/ui";
import { getIn, useFormik } from "formik";
import { initialValues, INPUTS_LIST } from "./FormAddingCallingList.const";
import cn from "classnames";
import styles from "./FormAddingCallingList.module.scss";
import { useAccessRight, useTranslate } from "@/hooks";
import { useAddCallingListMutation, useGetTimeintervalsQuery } from "@/store/RTKQuery/clsSlice";
import { getRobotList, selectRobotList } from "@/store/ses/sesRobotSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { SES } from "@/constants/accessRights";
import { getEndpointList, selectEndpointList } from "@/store/ses/sesEndpointSlice";
import { validationSchema } from "./FormAddingCallingList.validation";
import { useCookies } from "react-cookie";
import NoticeSingleAction from "@/ui/NoticeSingleAction/NoticeSingleAction";

const FormAddingCallingList: FC<IFormAddingCallingList> = ({ showModal, setShowModal }) => {
	const translate = useTranslate(); // hook для перевода текста
	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const [cookies, setCookie] = useCookies(["cls"]);

	const [addCallingList] = useAddCallingListMutation();
	const robotList = useAppSelector(selectRobotList);
	const endpointsData = useAppSelector(selectEndpointList);
	const dispatch = useAppDispatch();
	const { data: timeintervals, refetch } = useGetTimeintervalsQuery(undefined, {
		refetchOnMountOrArgChange: false,
		refetchOnReconnect: false,
	});

	const [endpointsList, setEndpointsList] = useState<{ label: string; value: string }[]>([]);
	const { data: timeintervalsList } = useGetTimeintervalsQuery();

	const [showNotification, setShowNotification] = useState<boolean>(false); // показ уведомления
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [submitted, setSubmitted] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const firstInputRef = useRef<HTMLInputElement | null>(null);

	const formik = useFormik({
		initialValues,
		validationSchema,
		validateOnChange: submitted,
		validateOnBlur: submitted,
		onSubmit: (values) => {
			if (isSubmitting) return;

			setIsSubmitting(true);
			handleShowNotice("notice_listIdAdding");

			if (values.robotId !== "") {
				addCallingList({
					newListData: {
						name: values.name,
						source: values.source,
						dialed: 0,
						timeintervals: values.timeintervals,
						description: "",
						channel: values.channel,
						channel_parameters: {
							robot_id: values.robotId || "",
							endpoint: (endpointsList.map((el) => el.value).includes(values.endpointId as string) ? values.endpointId : "-") ?? "-",
						},
						script_before_call: "",
						script_after_call: "",
						script: "",
						autoload: false,
						scheduler: [],
						items: [],
					},
				})
					.unwrap()
					.then((res) => {
						if (res.error === 0) {
							handleShowNotice("notice_listAdded");
							setCookie("cls", {
								...cookies["cls"],
								displayMode: JSON.stringify({ selectedCallList: res.id, currentChannel: "" }),
							});
							handleClose();
						} else if (res.error === 1) {
							handleShowNotice(`${translate("notice_listAddError")}: ${res.message}`);
						}
					})
					.catch((err) => {
						handleShowNotice(`${translate("notice_listAddError")}: ${JSON.stringify(err)}`);
					})
					.finally(() => {
						setIsSubmitting(false);
					});
			} else {
				addCallingList({
					newListData: {
						name: values.name,
						source: values.source,
						description: "",
						limits: {
							no_answer_timeout: 10,
							dial_tries: 1,
							max_channels: 2,
							redial_timeout: 600,
							watchdog: 240,
						},
						dialed: 0,
						timeintervals: values.timeintervals,
						channel: values.channel,
						script_before_call: "",
						script_after_call: "",
						script: "",
						autoload: false,
						scheduler: [],
						items: [],
					},
				})
					.unwrap()
					.then((res) => {
						if (res.error === 0) {
							handleShowNotice("notice_listAdded");
							setCookie("cls", {
								...cookies["cls"],
								displayMode: JSON.stringify({ selectedCallList: res.id, currentChannel: "" }),
							});
							handleClose();
						} else if (res.error === 1) {
							handleShowNotice(`${translate("notice_listAddError")}: ${res.message}`);
						}
					})
					.catch((err) => {
						handleShowNotice(`${translate("notice_listAddError")}: ${JSON.stringify(err)}`);
					})
					.finally(() => {
						setIsSubmitting(false);
					});
			}
		},
	});

	// обработчик закрытия формы
	const handleClose = (): void => {
		formik.resetForm();
		formik.setValues(initialValues);
		setShowModal(false);
	};
	//показать уведомление с сообщением
	const handleShowNotice = (message: string) => {
		setSnackbarMessage(message);
		setShowNotification(true);
	};

	useEffect(() => {
		if (showModal && firstInputRef.current) {
			setTimeout(() => firstInputRef.current?.focus(), 1000);
		}
	}, [showModal]);

	useEffect(() => {
		isAccess(SES.ROBOT_LIST) && dispatch(getRobotList()); // получаем список роботов
	}, []);

	useEffect(() => {
		if (formik.values.channel === "telegram") {
			if (robotList?.data?.length) {
				formik.setValues((prev) => ({ ...prev, robotId: robotList.data[0].id }));
			} else {
				formik.setValues((prev) => ({ ...prev, robotId: "" }));
			}
		} else {
			formik.setValues((prev) => ({ ...prev, robotId: "" }));
		}
	}, [formik.values.channel, robotList]);

	useEffect(() => {
		if (formik.values.robotId && isAccess(SES.ENDPOINT_LIST)) {
			dispatch(getEndpointList(formik.values.robotId));
		}
	}, [formik.values.robotId]);

	useEffect(() => {
		const manualEndpoints = endpointsData.data.filter((endpoint) => endpoint.entryType === "manual") ?? [];
		if (manualEndpoints.length > 0) {
			setEndpointsList(
				manualEndpoints.map((endpoint) => ({
					label: endpoint.name,
					value: endpoint.id,
				}))
			);
		} else {
			setEndpointsList([]);
			formik.setFieldValue("endpointId", translate("title_notFound"));
		}
	}, [endpointsData.data]);

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		formik.handleSubmit();
		setSubmitted(true);
	};

	const handleChangeChannel = (value: string) => {
		if (value === "telegram" || value === "voice") {
			formik.setValues((prev) => ({ ...prev, channel: value }));
		}
	};

	return (
		<>
			<ModalFormWindow
				showModal={showModal}
				setShowModal={isSubmitting ? undefined : setShowModal}
				headerTitle="button_addList"
				close={isSubmitting ? undefined : handleClose}
			>
				{timeintervals?.error === 1 ? (
					<BlockWithError updateHandler={refetch} />
				) : (
					<form className={cn(styles["input-list"])} onSubmit={handleSubmit} noValidate>
						{INPUTS_LIST.map((el, i) => {
							const fieldError = getIn(formik.errors, el.title);
							const fieldTouched = getIn(formik.touched, el.title);

							return el.type === "select" ? (
								el.title === "timeintervals" ? (
									<CustomMultiSelect
										key={el.title}
										variantsList={timeintervalsList?.timeintervals.map((el) => ({ title: el.name, value: el.id })) ?? []}
										label={translate("select_timeintervalsList")}
										setState={(val) => formik.setFieldValue(`timeintervals`, val)}
										values={formik.values.timeintervals}
										elementWidth="full"
										height={20}
									/>
								) : (
									<CustomSelect
										key={el.title}
										title={translate(el.label)}
										variantsList={el.variants}
										elementWidth="full"
										setState={handleChangeChannel}
										height={33}
										mt={"0"}
									/>
								)
							) : (
								<CustomTextfieldWithTouched
									key={el.title}
									type={el.type}
									autoFocus={i === 0}
									name={el.title}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									label={
										el.title === "source"
											? translate(typeof el.label === "string" ? el.label : el.label[formik.values.channel as keyof typeof el.label])
											: translate(typeof el.label === "string" ? el.label : el.label[formik.values.channel as keyof typeof el.label])
									}
									value={formik.values[el.title as keyof TInitialValues]}
									required={true}
									error={Boolean(fieldError && fieldTouched)}
									touched={Boolean(fieldTouched)}
									// helperText={fieldTouched && fieldError ? String(fieldError) : ""}
								/>
							);
						})}

						{formik.values.channel === "telegram" && (
							<>
								<TextfieldWithAutocomplete
									autocompleteOptionsWithId={robotList.data.map((robot) => ({ value: robot.id, label: robot.name }))}
									value={formik.values.robotId as string} // здесь id выбранного робота
									setValues={formik.setValues}
									values={formik.values}
									onChange={(e) => formik.handleChange(e)}
									onBlur={() => {
										const options = robotList.data.map((robot) => robot.id);
										const currentValue = formik.values.robotId;

										// если текущее значение не найдено среди вариантов — откатываем
										if (!options.includes(currentValue as string)) {
											formik.setFieldValue("robotId", robotList.data[0].id);
										}
									}}
									name="robotId"
									label={translate("select_robot")}
								/>
								<TextfieldWithAutocomplete
									autocompleteOptionsWithId={endpointsList}
									value={formik.values.endpointId as string} // здесь id выбранной КТ
									setValues={formik.setValues}
									values={formik.values}
									onChange={(e) => formik.handleChange(e)}
									name="endpointId"
									label={translate("select_endpoint")}
								/>
							</>
						)}
						<CustomButton type="submit" disabled={!formik.isValid || isSubmitting}>
							{translate("button_add")}
						</CustomButton>
					</form>
				)}
			</ModalFormWindow>
			<NoticeSingleAction
				severity={snackbarMessage.toLowerCase().includes("error") || snackbarMessage.toLowerCase().includes("ошибка") ? "error" : "info"}
				showNotification={showNotification}
				setShowNotification={setShowNotification}
				title={snackbarMessage}
			/>
		</>
	);
};

export default FormAddingCallingList;
